import { EventBus } from './EventBus';
import { TabVisibilityMonitor } from './TabVisibilityMonitor';
import { HeartbeatLoop } from './HeartbeatLoop';
import { ApiClient } from './api/ApiClient';

import { BrowserBroadcastService } from './services/browser/BroadcastService';
import { BrowserDomEventService } from './services/browser/DomEventService';
import { BrowserFetchService } from './services/browser/FetchService';
import { BrowserLocalStorageService } from './services/browser/LocalStorageService';
import { BrowserLocationService } from './services/browser/LocationService';
import { BrowserNavigatorService } from './services/browser/NavigatorService';
import { BrowserSessionStorageService } from './services/browser/SessionStorageService';
import { BrowserTimerService } from './services/browser/TimerService';

import { TabService } from './services/TabService';
import { DeviceService } from './services/DeviceService';
import { CustomerService } from './services/CustomerService';
import { PageContextService } from './services/PageContextService';
import { MeasurementStore } from './services/MeasurementStore';
import type { TrackingDebugService } from './services/TrackingDebugService';

import type { PlatformAdapter } from './adapters/PlatformAdapter';
import { detachAdapterMeasurementListeners } from './adapters/detachAdapterMeasurementListeners';
import { BROADCAST_CHANNEL_NAME } from './config/broadcast';
import { StorageKey } from './config/storageKeys';
import { TrackingBusEvent } from './constants/trackingBusEvent';
import { TrackingTag } from './constants/trackingTag';

interface TrackingClientOptions {
  apiUrl: string;
  /** Full storefront domain URL for this page; forwarded to {@link ApiClient} for project resolution. */
  storefrontUrl?: string;
  adapter: PlatformAdapter;
  debug?: TrackingDebugService;
}

/**
 * Top-level wiring: consent-gated cold start, platform adapter hooks, and teardown on withdrawn consent.
 *
 * **Cold start:** `initialize` fetches a signed collect URL, then services subscribe to the bus and the
 * heartbeat loop runs. Each cold start builds a **fresh** `EventBus` so listeners from a previous run
 * (e.g. before consent withdrawal) cannot leak — cross-tab `BroadcastChannel` subscriptions are tied to
 * that bus instance.
 *
 * **Teardown:** `teardownOnWithdrawn` clears storage keys explicitly because a run might exit before
 * every service had `init()`; duplicates defensively what `consent:withdrawn` handlers do.
 *
 * **Measurements:** `disposeRuntime` and `teardownOnWithdrawn` call `PlatformAdapter.detachMeasurementListeners`
 * so a failed `coldStart` after a successful attach, or consent withdrawal without a reload, cannot leave
 * PDP DOM listeners pointing at an orphaned `MeasurementStore`.
 */
export class TrackingClient {
  private initialized = false;
  private coldStarting = false;
  /** Set when `/tracking/initialize` returns any-null in `{ url, token, ttl }` — tracking is off for this project. Page-lifetime only. */
  private deactivated = false;

  // Browser API services
  private sessionStorage = new BrowserSessionStorageService();
  private localStorage = new BrowserLocalStorageService();
  private fetcher = new BrowserFetchService();
  private events = new BrowserDomEventService();
  private timers = new BrowserTimerService();
  private location = new BrowserLocationService();
  private navigator = new BrowserNavigatorService();
  private broadcast = new BrowserBroadcastService(BROADCAST_CHANNEL_NAME);

  // Core wiring (recreated on cold-start)
  private bus: EventBus | null = null;
  private tabVisibility: TabVisibilityMonitor | null = null;
  private tab: TabService | null = null;
  private device: DeviceService | null = null;
  private customer: CustomerService | null = null;
  private pageContext: PageContextService | null = null;
  private measurements: MeasurementStore | null = null;
  private api: ApiClient | null = null;
  private heartbeat: HeartbeatLoop | null = null;

  constructor(private readonly options: TrackingClientOptions) {}

  init(): void {
    const debug = this.options.debug;
    // Create an initial bus early so we can emit consent events.
    this.bus = new EventBus(this.broadcast, debug);

    // Step -1: Register consent observer unconditionally.
    this.options.adapter.onConsentChange((hasConsent) => {
      if (hasConsent) {
        // Avoid stacking multiple cold starts (e.g. consent polling initial edge).
        if (this.initialized || this.coldStarting) {
          return;
        }
        if (this.deactivated) {
          debug?.log('TrackingClient', 'consent granted ignored: deactivated for this page', {});

          return;
        }
        this.bus?.emit(TrackingBusEvent.ConsentGranted, undefined, { publisher: TrackingTag.TrackingClient });
        void this.coldStart();
      } else {
        this.bus?.emit(TrackingBusEvent.ConsentWithdrawn, undefined, {
          publisher: TrackingTag.TrackingClient,
        });
        this.teardownOnWithdrawn();
      }
    });

    // Step 0: Check consent before doing anything else.
    if (!this.options.adapter.hasConsent()) {
      debug?.log('TrackingClient', 'init skipped: no consent yet', {});

      return;
    }

    void this.coldStart();
  }

  private async coldStart(): Promise<void> {
    const debug = this.options.debug;
    if (this.coldStarting) {
      return;
    }
    if (this.deactivated) {
      debug?.log('TrackingClient', 'coldStart skipped: deactivated for this page', {});

      return;
    }
    this.coldStarting = true;
    debug?.log('TrackingClient', 'coldStart begin', {});

    // Never reuse instances from a previous run.
    this.disposeRuntime();
    this.createRuntime();

    const bus = this.bus!;
    const api = this.api!;

    try {
      // Gate again: consent can change between init() and now.
      if (!this.options.adapter.hasConsent()) {
        debug?.log('TrackingClient', 'coldStart aborted: consent withdrawn', {});

        return;
      }

      const ok = await api.initialize();
      if (!ok) {
        debug?.log('TrackingClient', 'coldStart stopped: api.initialize failed', { deactivated: this.deactivated });

        return;
      }

      this.customer!.init();
      this.device!.init();
      this.tab!.init();
      await this.pageContext!.init();

      // Per-product index: zero the heartbeat index when this load is a different product than the
      // last one seen in this tab (sessionStorage survives the reload). No-op on a same-product reload.
      this.tab!.resetIndexOnProductChange(this.pageContext!.get().pageData.product_id);

      this.options.adapter.attachMeasurementListeners(this.measurements!);
      this.options.adapter.attachPurchaseDetector(bus);

      this.options.adapter.onProductPageEnter(() => {
        void this.onProductNavigation();
      });

      this.tabVisibility!.start();
      this.heartbeat!.init();
      this.heartbeat!.start();

      this.initialized = true;
      debug?.log('TrackingClient', 'coldStart complete: running', {});
    } finally {
      this.coldStarting = false;
    }
  }

  private async onProductNavigation(): Promise<void> {
    this.options.debug?.log('TrackingClient', 'onProductNavigation', { initialized: this.initialized });
    if (!this.initialized) {
      return;
    }
    if (!this.heartbeat || !this.pageContext || !this.measurements || !this.tab) {
      return;
    }

    this.heartbeat.stop();
    await this.pageContext.refresh();
    /* `stop()` already sent a final collect; snapshot here only resets measurement counters for the new PDP. */
    this.measurements.snapshot();
    this.tab.resetIndexOnProductChange(this.pageContext.get().pageData.product_id);
    this.heartbeat.start();
  }

  private teardownOnWithdrawn(): void {
    this.options.debug?.log('TrackingClient', 'teardownOnWithdrawn', {});
    this.initialized = false;

    detachAdapterMeasurementListeners(this.options.adapter);

    // Stop anything that could still schedule or send data.
    this.heartbeat?.stop({ silent: true });
    this.api?.dispose();
    this.tabVisibility?.stop();

    // Service-level cleanup also happens via consent:withdrawn subscriptions,
    // but ensure storage keys are gone even if init never completed.
    this.sessionStorage.remove(StorageKey.TabId);
    this.sessionStorage.remove(StorageKey.TabLastActive);
    this.sessionStorage.remove(StorageKey.HeartbeatIndex);
    this.sessionStorage.remove(StorageKey.LastProductId);
    this.localStorage.remove(StorageKey.CustomerId);
    this.localStorage.remove(StorageKey.DeviceId);
  }

  /**
   * Tears down the heartbeat loop and adapter listeners when `/tracking/initialize`
   * reports the project is off. Identity storage is preserved (consent is still
   * granted; only the project flag is off). Page-lifetime only — a real reload
   * re-checks via a fresh `initialize`.
   */
  private teardownOnDeactivated(): void {
    this.options.debug?.log('TrackingClient', 'teardownOnDeactivated', {});
    this.deactivated = true;
    this.initialized = false;

    detachAdapterMeasurementListeners(this.options.adapter);
    this.heartbeat?.stop({ silent: true });
    this.api?.dispose();
    this.tabVisibility?.stop();
  }

  private createRuntime(): void {
    const debug = this.options.debug;
    // Recreate bus every run so cross-tab listeners are reset cleanly.
    this.bus = new EventBus(this.broadcast, debug);

    this.tabVisibility = new TabVisibilityMonitor(this.events, this.timers, this.bus, debug);

    // TabService before HeartbeatLoop so tab:active / tab:activity refresh last_active before heartbeat resume.
    this.tab = new TabService(this.sessionStorage, this.bus, debug);
    this.device = new DeviceService(this.localStorage, this.bus, debug);
    this.measurements = new MeasurementStore(debug);
    this.api = new ApiClient(
      { apiUrl: this.options.apiUrl, storefrontUrl: this.options.storefrontUrl },
      this.fetcher,
      this.timers,
      { onDeactivated: () => this.teardownOnDeactivated() },
      debug,
    );
    this.pageContext = new PageContextService(this.options.adapter, this.location, this.navigator, debug);
    this.customer = new CustomerService(this.localStorage, this.options.adapter, this.bus, debug);

    this.heartbeat = new HeartbeatLoop(this.bus, this.timers, this.api, this.pageContext, this.measurements, this.tab, this.customer, this.device, debug);
  }

  private disposeRuntime(): void {
    this.options.debug?.log('TrackingClient', 'disposeRuntime', {});
    detachAdapterMeasurementListeners(this.options.adapter);
    this.heartbeat?.stop({ silent: true });
    this.api?.dispose();
    this.tabVisibility?.stop();
    this.bus?.dispose();

    this.tabVisibility = null;
    this.tab = null;
    this.device = null;
    this.customer = null;
    this.pageContext = null;
    this.measurements = null;
    this.api = null;
    this.heartbeat = null;
    this.bus = null;
  }
}
